/**
 * reportsDrill.ts — pure logic for the Reports drill-through (§7).
 *
 * Kept react-free so the data-match rules (§7.C) are unit-testable: the
 * drilled deals must reconcile to the clicked aggregate — same slug set,
 * same UTC month boundary, same pence unit, and the rep drill's
 * won-this-quarter cohort judged against the SAME quarterStart the /reports
 * response carried (never a client-derived quarter).
 */
import type { SalesDeal } from '../../types'

export type ReportsDrill =
  | { kind: 'stage'; stage: string; label: string; count: number }
  // 'rep' carries the performer row's OWN key (E360-2): employeeId when the
  // row grouped by a linked employee, absent when it grouped by legacy text.
  // The fetch keys on whichever it is, so the drilled rows are exactly the
  // population the aggregate counted — the reconciliation below depends on it.
  | { kind: 'rep'; rep: string; employeeId?: string; openDeals: number; pipelinePence: number; wonThisQuarterPence: number; quarterStart: string }
  | { kind: 'month'; month: string; wonPence: number }
  // SB-12 dashboard drills. 'open' is the COMPOSITE open-stage drill — the
  // server has no "open" filter (ListDealsQuery is stage/assigned_to only),
  // so the view merges 4 per-stage calls and reconciles the MERGED set
  // against the server aggregate pair (count AND pence), never per call.
  // Open-pipeline-value and Open-deals cards share this one drill: they are
  // two figures over the SAME population.
  // §23 D-3: the Avg. deal size card drills to this SAME population — it
  // carries the server mean so the header can restate "£T across N open
  // deals · mean £A"; the gate is the count + sum below, so the mean
  // reconciles by construction. Fetch and gate are the 'open' kind's.
  | { kind: 'open'; openDeals: number; pipelinePence: number; avgPence?: number }
  // §23 D-4: the Weighted pipeline card. The SAME open population, with a
  // per-row Weighted column = ⌊basePence × effectiveProbability ÷ 100⌋ (the
  // SQL floors per row the same way), whose Σ must equal weightedPence
  // EXACTLY — the only shape in which the rows add up to the number clicked.
  | { kind: 'weighted'; openDeals: number; pipelinePence: number; weightedPence: number }
  // §23 D-5: the Avg. sales cycle card. stage=closed_won; rows without a
  // close stamp are dropped client-side AND counted; a per-row Days column;
  // the count gated against wonCount (ALL fetched won rows, dropped ones
  // included — the server counted them too); the client mean over the
  // listed rows compared to the server's meanDays at 0.1-day precision.
  | { kind: 'cycle'; wonCount: number; meanDays: number }
  // The win-rate cohort: closed_won + closed_lost rows, reconciled against
  // the server's whole-population wonCount/lostCount — the pct itself is
  // derived from those two counts, so counts are the reconciliation basis.
  | { kind: 'closed'; wonCount: number; lostCount: number }
  // SB-13: the deals-by-company rollup drill. UNLIKE every other kind this
  // one has NO server aggregate — the rollup is client-computed over the
  // page's own capped deal list, and the drill filters that SAME list (no
  // refetch), so drill and rollup are identical by construction. The echo
  // figures ride along (the 'rep' precedent) so the header can restate the
  // clicked row; company is matched by EXACT trimmed string — "Acme" and
  // "Acme Ltd" are different rows today (companies-as-first-class is the
  // out-of-scope fix, not drill-side cleverness).
  | { kind: 'company'; name: string; total: number; won: number; open: number; pipelinePence: number; wonPence: number }
  // §33 W-2: the Forecast grid's won cell — ONE rep's closed-won deals whose
  // closed_at falls INSIDE the grid's period, fenced (forecast.go step 2).
  // Every field is copied from the /forecast RESPONSE, never from the
  // picker's state, so the drill and the cell describe the same window (the
  // §7.C rule). NOT a reuse of 'rep': that kind is the /reports performer
  // row — every deal of the employee with no close range, gated as open rows
  // plus won-since-quarterStart — so from this grid it would list OPEN rows
  // under a WON cell, judge a month view against the current quarter, and
  // reconcile correctly on the default period (where /reports' quarter and
  // the grid's coincide to the penny on Fourth Coffee), passing its gate
  // exactly where anyone would test it. Gate: the 'open' shape — the fenced
  // row count against wonCount, then Σ basePence against wonPence.
  | { kind: 'forecastWon'; employeeId: string; employeeName: string; periodKind: 'month' | 'quarter'; periodStart: string; periodEnd: string; wonPence: number; wonCount: number; wonExcludedNonGbp: number }

/** UTC month key ("2026-03") from an RFC3339 timestamp — PARSE then UTC
 *  getters, never a string slice: "2026-09-01T00:30:00+01:00" slices to
 *  September but its UTC month (the aggregate's date_trunc basis) is
 *  August. */
export function monthKeyUTC(iso: string): string {
  const d = new Date(iso)
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`
}

/** The month drill's client-side cut. The server already filtered
 *  stage=closed_won; this applies the same UTC month boundary the
 *  aggregate's date_trunc('month', closed_at) used. */
export function filterMonthDeals(deals: SalesDeal[], month: string): SalesDeal[] {
  return deals.filter(d => d.closedAt != null && monthKeyUTC(d.closedAt) === month)
}

/** §23 D-4: one row's weighted pence, exactly as the server computes it —
 *  ⌊fenced × effective ÷ 100⌋ — or null when the row is outside the fence
 *  (uncaptured non-GBP: basePence null) or carries no resolved probability
 *  (a pre-2.2 wire). A null contributes NOTHING to the sum, the server's own
 *  skip, and the gate then says the shortfall instead of fabricating it. */
export function weightedRowPence(d: SalesDeal): number | null {
  if (d.basePence == null || d.effectiveProbability == null) return null
  return Math.floor((d.basePence * d.effectiveProbability) / 100)
}

/** §23 D-5: one won row's cycle in FRACTIONAL days (closedAt − createdAt), the
 *  server's AVG(EXTRACT(EPOCH …) / 86400) basis; null without both stamps. */
export function cycleDays(d: SalesDeal): number | null {
  if (!d.closedAt || !d.createdAt) return null
  const ms = Date.parse(d.closedAt) - Date.parse(d.createdAt)
  return Number.isFinite(ms) ? ms / 86_400_000 : null
}

/** The cycle drill lists the rows it can measure and COUNTS the rest. */
export function cycleRows(deals: SalesDeal[]): { listed: SalesDeal[]; dropped: number } {
  const listed = deals.filter(d => cycleDays(d) != null)
  return { listed, dropped: deals.length - listed.length }
}

/** Mean of the listed rows' fractional days, null when none. */
export function meanCycleDays(listed: SalesDeal[]): number | null {
  const days = listed.map(cycleDays).filter((v): v is number => v != null)
  return days.length === 0 ? null : days.reduce((s, v) => s + v, 0) / days.length
}

/** 0.1-day precision: the comparison D-5 names. True when the client mean
 *  over the listed rows and the server's figure disagree once both are
 *  rounded to a tenth — the cap, or dropped NULL-stamp rows, are the causes. */
export function cycleMeanDiffers(clientMean: number | null, serverMean: number): boolean {
  if (clientMean == null) return true
  return clientMean.toFixed(1) !== serverMean.toFixed(1)
}

/** §23 D-9: the rows the £ figure EXCLUDED — uncaptured non-GBP deals
 *  (basePence null on a non-GBP row). Derived from the rows themselves; 0
 *  today under capture-or-refuse, and the note renders nothing at 0. */
export function nonGbpExcludedCount(deals: SalesDeal[]): number {
  return deals.filter(d => d.basePence === null && d.currency !== 'GBP').length
}

/** §26 F-1a: the FENCED row count of a fetched set — the rows that produced
 *  the £ figure, i.e. every row minus the ones the fence dropped. The wire's
 *  counts (openDealCount, a performer's openDeals) are fenced since §26, so
 *  the gate compares this to them, never `deals.length`: the first uncaptured
 *  deal would otherwise read "Showing 6 of 5 deals — list calls cap at 200",
 *  a false message with a false cause. */
export function fencedRowCount(deals: SalesDeal[]): number {
  return deals.length - nonGbpExcludedCount(deals)
}

export type DrillShortfall =
  | { unit: 'deals'; shown: number; expected: number }
  | { unit: 'pence'; shown: number; expected: number }
  | { unit: 'rep' }

/** §7.C honesty gate: list calls cap at 200 while the aggregates are
 *  uncapped, so the drill can under-show — and MUST say so rather than
 *  render as if complete. Returns null only when the drilled rows reconcile
 *  EXACTLY to the clicked figure(s). */
export function drillShortfall(drill: ReportsDrill, deals: SalesDeal[]): DrillShortfall | null {
  switch (drill.kind) {
    case 'stage':
      return deals.length === drill.count
        ? null
        : { unit: 'deals', shown: deals.length, expected: drill.count }
    case 'month': {
      // Comparable-leg sums (0111): the server aggregate is whole — GBP
      // pence plus captured base — and skips uncaptured rows, so the
      // client cut must sum basePence (null → 0, the same skip).
      const sum = deals.reduce((s, d) => s + (d.basePence ?? 0), 0)
      return sum === drill.wonPence
        ? null
        : { unit: 'pence', shown: sum, expected: drill.wonPence }
    }
    case 'rep': {
      const open = deals.filter(d => d.stage !== 'Closed Won' && d.stage !== 'Closed Lost')
      const quarterMs = Date.parse(`${drill.quarterStart}T00:00:00Z`)
      const wonQSum = deals
        .filter(d => d.stage === 'Closed Won' && d.closedAt != null && Date.parse(d.closedAt) >= quarterMs)
        .reduce((s, d) => s + (d.basePence ?? 0), 0)
      const openSum = open.reduce((s, d) => s + (d.basePence ?? 0), 0)
      // §26 F-1a: the row's openDeals is FENCED — compare the fenced row count.
      return fencedRowCount(open) === drill.openDeals && openSum === drill.pipelinePence && wonQSum === drill.wonThisQuarterPence
        ? null
        : { unit: 'rep' }
    }
    case 'open': {
      // The MERGED per-stage sets against the aggregate pair. Count first
      // (the sharper message), then the comparable-leg sum — same skip rule
      // as the server (uncaptured rows contribute nothing to either side).
      // §26 F-1a: openDeals is the FENCED count, so the fetched set is
      // judged net of the rows the fence dropped — those are LISTED (the
      // drill shows every fetched row) and named by the excluded note.
      if (fencedRowCount(deals) !== drill.openDeals) {
        return { unit: 'deals', shown: fencedRowCount(deals), expected: drill.openDeals }
      }
      const sum = deals.reduce((s, d) => s + (d.basePence ?? 0), 0)
      return sum === drill.pipelinePence
        ? null
        : { unit: 'pence', shown: sum, expected: drill.pipelinePence }
    }
    case 'closed': {
      // Reconcile per outcome — a won shortfall masked by a lost surplus
      // must still be SAID, so compare the split, not just the total.
      const won = deals.filter(d => d.stage === 'Closed Won').length
      const lost = deals.filter(d => d.stage === 'Closed Lost').length
      return won === drill.wonCount && lost === drill.lostCount
        ? null
        : { unit: 'deals', shown: won + lost, expected: drill.wonCount + drill.lostCount }
    }
    case 'weighted': {
      // The same merged open set: count first, then Σ of the per-row
      // weighted column against the card — EXACT, since both floor per row
      // and both skip the uncaptured (null → nothing).
      if (fencedRowCount(deals) !== drill.openDeals) {
        return { unit: 'deals', shown: fencedRowCount(deals), expected: drill.openDeals }
      }
      const sum = deals.reduce((s, d) => s + (weightedRowPence(d) ?? 0), 0)
      return sum === drill.weightedPence
        ? null
        : { unit: 'pence', shown: sum, expected: drill.weightedPence }
    }
    case 'cycle':
      // Gate the COUNT of every fetched won row — the server's wonCount
      // counts NULL-stamp rows too; the view drops those from the list and
      // says how many. The mean is judged separately (cycleMeanDiffers).
      return deals.length === drill.wonCount
        ? null
        : { unit: 'deals', shown: deals.length, expected: drill.wonCount }
    case 'company':
      // No server aggregate exists for a company group, so there is nothing
      // real to reconcile against — and a same-source comparison would always
      // pass, which is a fake reconciliation, not a gate. The view states the
      // 200-row cap honestly instead (SB-13 pin).
      return null
    case 'forecastWon': {
      // §33 W-2: the fetched set is the server's own predicate (stage,
      // employee, close window), so it reconciles by construction unless the
      // cap bit or the window was sent wrong — count first (the sharper
      // message), then the comparable-leg sum, both net of the rows the
      // fence dropped, exactly as wonCount / wonPence were computed.
      if (fencedRowCount(deals) !== drill.wonCount) {
        return { unit: 'deals', shown: fencedRowCount(deals), expected: drill.wonCount }
      }
      const sum = deals.reduce((s, d) => s + (d.basePence ?? 0), 0)
      return sum === drill.wonPence
        ? null
        : { unit: 'pence', shown: sum, expected: drill.wonPence }
    }
  }
}
